import { LinkType } from '@website-builder/model/domain/link/link-type.enum';
import { TbLink } from '@website-builder/model/domain/link/tb-link';
import { ButtonActionType } from '@website-builder/widgets/button/model/button-action-type.enum';
import { BuilderElementType } from '@website-builder/model/domain/builder-element-type.enum';
import { LinkLike } from '@website-builder/model/domain/link/link-like.type';

export class PageReferenceUrl {
  static readonly PAGE_REFERENCE_PATTERN = /^page:\s*/i;
  static readonly ABSOLUTE_URL_PATTERN = /^[a-z][a-z\d+.-]*:\/\//i;

  static normalizePageReferenceUrl(url: string | null | undefined): string {
    return (url ?? '').trim().replace(this.PAGE_REFERENCE_PATTERN, '');
  }

  static isPageReferenceUrl(url: string | null | undefined): boolean {
    return this.PAGE_REFERENCE_PATTERN.test((url ?? '').trim());
  }

  static pageReferencesEqual(
    currentPageReference: string | null | undefined,
    nextPageReference: string | null | undefined,
  ): boolean {
    const normalizedCurrentReference = this.normalizePageReferenceUrl(currentPageReference);
    const normalizedNextReference = this.normalizePageReferenceUrl(nextPageReference);

    return !!normalizedCurrentReference && normalizedCurrentReference === normalizedNextReference;
  }

  static itemReferencesPage(item: unknown, pageLink: string): boolean {
    return this.#getCandidateLinks(item).some(link => this.#isInternalPageLink(link, pageLink));
  }

  static replacePageReference(item: unknown, previousPageLink: string, replacementLink: string | TbLink): void {
    this.#getCandidateLinks(item).forEach(link => {
      if (!this.#isInternalPageLink(link, previousPageLink)) {
        return;
      }

      if (typeof replacementLink === 'string') {
        this.#replaceWithPageLink(link, previousPageLink, replacementLink);
        return;
      }

      link.url = replacementLink.url;
      link.type = replacementLink.type;
      link.target = replacementLink.target;
      link.displayedUrl = replacementLink.displayedUrl;
    });
  }

  static #getCandidateLinks(item: unknown): LinkLike[] {
    if (!item) {
      return [];
    }

    if (Array.isArray(item)) {
      return item.flatMap(entry => this.#getCandidateLinks(entry));
    }

    if (typeof item !== 'object') {
      return [];
    }

    const links: LinkLike[] = [];
    const carrier = item as {
      buttonLink?: LinkLike | null;
      cells?: unknown[];
      elements?: unknown[];
      items?: unknown[];
      link?: LinkLike | null;
      menuSections?: { items?: unknown[] }[];
    };

    if (carrier.link && typeof carrier.link.url === 'string') {
      links.push(carrier.link);
    }

    if (
      item['type'] === BuilderElementType.Panel &&
      item['buttonActionType'] === ButtonActionType.LINK &&
      carrier.buttonLink
    ) {
      links.push(carrier.buttonLink);
    }

    if (carrier.elements) {
      links.push(...this.#getCandidateLinks(carrier.elements));
    }

    if (carrier.cells) {
      links.push(...this.#getCandidateLinks(carrier.cells));
    }

    if (carrier.items) {
      links.push(...this.#getCandidateLinks(carrier.items));
    }

    if (carrier.menuSections) {
      links.push(...carrier.menuSections.flatMap(section => this.#getCandidateLinks(section.items ?? [])));
    }

    return links;
  }

  static #isInternalPageLink(link: LinkLike, pageLink: string): boolean {
    if (!this.pageReferencesEqual(link.url, pageLink)) {
      return false;
    }

    return this.isPageReferenceUrl(link.url) || link.type === LinkType.Page || !this.#isAbsoluteUrl(link.url);
  }

  static #isAbsoluteUrl(url: string | null | undefined): boolean {
    return this.ABSOLUTE_URL_PATTERN.test((url ?? '').trim());
  }

  static #replaceWithPageLink(link: LinkLike, previousPageLink: string, nextPageLink: string): void {
    const normalizedNextPageLink = this.normalizePageReferenceUrl(nextPageLink);
    const shouldUpdateDisplayedUrl = this.#shouldReplaceDisplayedUrl(link, previousPageLink);

    link.url = `PAGE:${normalizedNextPageLink}`;

    if (shouldUpdateDisplayedUrl) {
      link.displayedUrl = normalizedNextPageLink;
    }
  }

  static #shouldReplaceDisplayedUrl(link: LinkLike, previousPageLink: string): boolean {
    const normalizedPreviousPageLink = this.normalizePageReferenceUrl(previousPageLink);
    const normalizedDisplayedUrl = this.normalizePageReferenceUrl(link.displayedUrl);
    const normalizedCurrentLinkUrl = this.normalizePageReferenceUrl(link.url);

    return normalizedDisplayedUrl === normalizedPreviousPageLink || normalizedDisplayedUrl === normalizedCurrentLinkUrl;
  }
}
